import React, { useEffect, useState } from 'react';
import { Plus, Trash2, FlaskConical, Save, Sparkles, Wand2 } from 'lucide-react';
import docshawkService, { CandidateField, SourceDocument } from '../../services/docshawkService';
import { PageHeader, Spinner, Button, IconButton } from '../../components/ui';
import RegexTester from '../../components/RegexTester';
import '../../styles/theme.css';

const TYPES = ['string', 'number', 'currency', 'date', 'percentage', 'boolean', 'duration'];
const blank = (): CandidateField => ({ key: '', label: '', data_type: 'string', regex_hint: '', description: '', required: false });

const Parameters: React.FC = () => {
  const [rows, setRows] = useState<CandidateField[]>([]);
  const [docs, setDocs] = useState<SourceDocument[]>([]);
  const [testing, setTesting] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [suggestDocId, setSuggestDocId] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [wholeDoc, setWholeDoc] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [schema, docList] = await Promise.all([
          docshawkService.getActiveSchema(),
          docshawkService.listDocuments(1, 50).catch(() => ({ documents: [] as SourceDocument[] })),
        ]);
        setRows(schema.current_version?.fields?.length
          ? schema.current_version.fields.map((f) => ({ ...f }))
          : [blank()]);
        const dl = (docList as any).documents || [];
        setDocs(dl);
        if (dl[0]) setSuggestDocId(dl[0].id);
      } finally { setLoading(false); }
    })();
  }, []);

  const suggestFromDoc = async () => {
    if (!suggestDocId) return;
    setSuggesting(true); setMessage('');
    try {
      const res = await docshawkService.suggestParameters(suggestDocId, wholeDoc);
      if (res.error) { setMessage(`Suggestion failed: ${res.error}`); return; }
      setRows((cur) => {
        const have = new Set(cur.filter((r) => r.key.trim()).map((r) => r.key));
        const additions = res.fields.filter((f) => !have.has(f.key));
        const base = cur.filter((r) => r.key.trim());  // drop the empty starter row
        return [...base, ...additions.map((f) => ({ ...f }))];
      });
      setMessage(`Suggested ${res.fields.length} parameters from the document (via ${res.model || 'LLM'}). Review, edit, and save.`);
    } catch (e: any) {
      setMessage(e?.response?.data?.detail || 'Suggestion failed');
    } finally { setSuggesting(false); }
  };

  const upd = (i: number, patch: Partial<CandidateField>) =>
    setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const remove = (i: number) => { setRows((r) => r.filter((_, j) => j !== i)); if (testing === i) setTesting(null); };

  const loadTemplate = async () => {
    const { fields } = await docshawkService.fieldTemplates();
    setRows(fields.map((f) => ({ ...f })));
    setMessage('Loaded docshawk template — edit and save.');
  };

  const save = async () => {
    const fields = rows.filter((r) => r.key.trim());
    if (!fields.length) { setMessage('Add at least one parameter with a key.'); return; }
    setSaving(true); setMessage('');
    try {
      const res = await docshawkService.setSchema(fields);
      setMessage(`Saved ${res.current_version.fields.length} parameters (v${res.current_version.version_number}). New documents will use these.`);
    } catch (e: any) {
      setMessage(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div><PageHeader title="Terms Checklist" /><div className="mx-card mx-card-pad"><Spinner label="Loading…" /></div></div>;

  return (
    <div>
      <PageHeader title="Terms Checklist"
        subtitle="The terms DocShawk looks for in every agreement. Each has a plain-English description that guides the AI; an optional pattern also matches it with fixed rules."
        actions={<>
          {docs.length > 0 && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <select className="mx-select" style={{ width: 'auto', maxWidth: 200 }}
                value={suggestDocId} onChange={(e) => setSuggestDocId(e.target.value)}>
                {docs.map((d) => <option key={d.id} value={d.id}>{d.filename}</option>)}
              </select>
              <Button icon={Wand2} variant="secondary" size="sm" onClick={suggestFromDoc} disabled={suggesting}>
                {suggesting ? 'Reading…' : 'Suggest from document'}</Button>
              <label className="mx-subtle" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                title="Chunk the whole document (more LLM calls) instead of just the first section">
                <input type="checkbox" checked={wholeDoc} onChange={(e) => setWholeDoc(e.target.checked)} /> whole doc
              </label>
            </div>
          )}
          <Button icon={Sparkles} variant="secondary" size="sm" onClick={loadTemplate}>Load template</Button>
          <Button icon={Save} variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save parameters'}</Button>
        </>} />

      {message && <div className="mx-card mx-card-pad" style={{ marginBottom: 16, fontSize: 13 }}>{message}</div>}

      <div className="mx-card" style={{ overflow: 'visible' }}>
        <table className="mx-table">
          <thead><tr>
            <th style={{ width: 150 }}>Key</th><th>Label</th><th style={{ width: 120 }}>Type</th>
            <th>Regex (deterministic)</th><th>Description (LLM)</th>
            <th style={{ width: 70 }}>Required</th><th style={{ width: 90 }}></th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <React.Fragment key={i}>
                <tr>
                  <td><input className="mx-input mx-mono" style={cell} placeholder="leverage_ratio"
                    value={r.key} onChange={(e) => upd(i, { key: e.target.value })} /></td>
                  <td><input className="mx-input" style={cell} placeholder="Leverage Ratio"
                    value={r.label || ''} onChange={(e) => upd(i, { label: e.target.value })} /></td>
                  <td><select className="mx-select" style={cell} value={r.data_type || 'string'}
                    onChange={(e) => upd(i, { data_type: e.target.value })}>
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></td>
                  <td><input className="mx-input mx-mono" style={{ ...cell, fontSize: 12,
                    borderColor: testing === i ? 'var(--mx-accent)' : undefined }}
                    placeholder="…not to exceed ([0-9.]+:[0-9.]+)"
                    value={r.regex_hint || ''} onChange={(e) => upd(i, { regex_hint: e.target.value })} /></td>
                  <td><input className="mx-input" style={cell} placeholder="Max permitted leverage ratio"
                    value={r.description || ''} onChange={(e) => upd(i, { description: e.target.value })} /></td>
                  <td style={{ textAlign: 'center' }}><input type="checkbox" checked={!!r.required}
                    onChange={(e) => upd(i, { required: e.target.checked })} /></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <IconButton icon={FlaskConical} label="Test regex"
                      onClick={() => setTesting(testing === i ? null : i)} />
                    <IconButton icon={Trash2} label="Remove" style={{ color: 'var(--mx-danger)' }}
                      onClick={() => remove(i)} />
                  </td>
                </tr>
                {testing === i && (
                  <tr><td colSpan={7} style={{ padding: '0 16px 12px' }}>
                    <RegexTester pattern={r.regex_hint || ''} documents={docs}
                      onChangePattern={(p) => upd(i, { regex_hint: p })} onClose={() => setTesting(null)} />
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <div className="mx-card-pad" style={{ borderTop: '1px solid var(--mx-border)' }}>
          <Button icon={Plus} variant="secondary" size="sm" onClick={() => setRows((r) => [...r, blank()])}>
            Add parameter</Button>
        </div>
      </div>
    </div>
  );
};

const cell: React.CSSProperties = { padding: '6px 8px', fontSize: 13 };

export default Parameters;
